<template>
  <div class="relative flex h-full flex-col gap-4 overflow-visible">
    <div :class="dataTableHeadFilterClass">
      <slot name="actionsStart" />

      <InfuseInput
        v-if="search"
        :model-value="search.query"
        :placeholder="search.placeholder"
        name="search"
        size="xs"
        autofocus
        class="grow-1"
        @update:model-value="debounceSearch"
      >
        <font-awesome-icon :icon="['fas', 'magnifying-glass']" size="1x" class="text-neutral-300" />

        <template #clear="{ value }">
          <font-awesome-icon
            v-if="value"
            :icon="['fas', 'xmark']"
            size="1x"
            class="cursor-pointer text-neutral-300 hover:text-primary-500 active:text-primary-700"
            @click="emit('search', '')"
          />
        </template>
      </InfuseInput>

      <slot name="actions" />

      <InfuseIconButton v-if="refreshable" :icon="['fas', 'refresh']" class="pr-6" size="sm" @click="emit('refresh')" />
    </div>

    <div v-if="filters.length" class="flex flex-wrap gap-2">
      <div v-for="filter in filters" :key="filter.config.name" class="flex gap-2">
        <InfusePill class="select-none" closable :label="filter.config.label()" @click="emit('remove:filter', filter)">
          {{
            `${determineOperator(filter)} ${
              filter.searchQuery ? filter.searchQuery.toLowerCase() : filter.selectedOption.label().toLowerCase()
            }`
          }}
        </InfusePill>
      </div>
    </div>

    <div v-if="loading && rows.length === 0" class="mt-16 flex w-full justify-center">
      <InfuseSpinner size="large" />
    </div>

    <div v-else ref="dataTable" class="relative h-full overflow-y-visible" :class="dataTableClass">
      <table class="relative w-full text-left">
        <thead
          class="sticky z-10 hidden w-full text-xs font-medium capitalize md:table-header-group"
          :class="dataTableHeadClass"
        >
          <tr>
            <th v-if="selectable" scope="col" class="py-3 pr-3 pl-6">
              <div class="flex items-center">
                <InfuseCheckBox
                  id="checkbox-all"
                  :model-value="selectedRows.length === rows.length"
                  @update:model-value="$event ? selectAll() : deselectAll()"
                />
              </div>
            </th>

            <th
              v-for="column in columnsWithSortState"
              :key="column.name"
              scope="col"
              class="cursor-pointer overflow-hidden text-ellipsis py-3 px-6 font-medium"
              :class="[
                column.align === UiTableColumnAlignment.CENTER ? 'text-center' : '',
                column.align === UiTableColumnAlignment.LEFT ? 'text-left' : '',
                column.align === UiTableColumnAlignment.RIGHT ? 'text-right' : '',
                column.maxWidth ? `max-w-[${column.maxWidth}]` : ''
              ]"
              @click="() => column.sortable && sort(column.state)"
            >
              <span class="inline line-clamp-1">
                {{ column.label() }}
                <font-awesome-icon v-if="column.sortable" class="ml-1" :icon="getSortIcon(column.state.order)" />
                <InfuseTooltip
                  v-if="column.tooltip"
                  :content="column.tooltip()"
                  placement="top"
                  offset-distance="6"
                  class="normal-case"
                >
                  <font-awesome-icon
                    :icon="['fal', 'circle-info']"
                    class="ml-1 cursor-help text-neutral-400 hover:text-neutral-600"
                    @click.stop
                  />
                </InfuseTooltip>
              </span>
            </th>

            <th v-if="actions.length > 0" scope="col" class="py-3 px-6">
              <span class="sr-only">{{ t('shared.component.infuse.table.header.actions') }}</span>
            </th>

            <th v-if="downloadable || editable || removable" scope="col" class="py-3 px-6">
              <span class="sr-only">{{ t('shared.component.infuse.table.header.editRemove') }}</span>
            </th>
          </tr>
        </thead>

        <slot v-if="!loading && rows.length === 0" name="noResults">
          <tr>
            <td :colspan="columns.length + (downloadable || editable || removable ? 1 : 0)">
              <div class="my-20 flex w-full max-w-[550px] flex-col gap-8 justify-self-center text-center">
                <div class="flex flex-col items-center gap-4 overflow-visible">
                  <div class="h-40 w-40 bg-empty-state bg-contain bg-center bg-no-repeat" />
                  <p class="-mt-14 text-center text-xl font-bold">{{ noDataTitle || t('common.noData') }}</p>
                  <p class="-mt-4 whitespace-pre-line text-center text-sm">
                    {{ noDataDescription || t('common.noDataDescription') }}
                  </p>

                  <slot name="noResultsActions" />
                </div>
              </div>
            </td>
          </tr>
        </slot>

        <tbody class="text-xs font-medium" :class="dataTableBodyClass">
          <tr
            v-for="(row, i) in rows"
            :key="`${row[rowKey]}-${i}`"
            class="observable group cursor-pointer"
            :class="[
              active(row, i) ? 'text-primary-500 ' : '',
              selectedRows.includes(row) ? 'bg-neutral-50' : '',
              selectedRows.length === 0 && highlightRow === i ? 'bg-neutral-50' : '',
              disabledRows.includes(row) ? 'opacity-50' : '',
              row.readonly ? 'cursor-text text-neutral-400' : '',
              dataTableBodyRowClass
            ]"
            @click.stop="clickRow(row)"
          >
            <td
              v-if="selectable"
              :class="[
                verticalSize === 'compact' ? 'py-1 px-6' : '',
                verticalSize === 'medium' ? 'py-2 px-6' : '',
                verticalSize === 'expanded' ? 'py-3 px-6' : ''
              ]"
              class="w-4 rounded-l-md"
              @click.stop=""
            >
              <div class="flex items-center">
                <slot name="selectRow" :data="row">
                  <InfuseCheckBox
                    :model-value="selectedRows.includes(row)"
                    :disabled="disabledRows.includes(row)"
                    @update:model-value="selectRow($event, row)"
                  />
                </slot>
              </div>
            </td>

            <td
              v-for="(column, j) in columns"
              :key="`column-field-attr-${j}`"
              class="hidden md:table-cell overflow-hidden break-all"
              :class="[
                column.bold
                  ? active(row, i)
                    ? 'font-semibold text-primary-500'
                    : 'font-semibold text-neutral-700'
                  : '',
                verticalSize === 'compact' ? 'whitespace-nowrap py-1 px-6' : '',
                verticalSize === 'medium' ? 'whitespace-nowrap py-2 px-6' : '',
                verticalSize === 'expanded' ? 'whitespace-nowrap py-3 px-6' : '',
                column.align === UiTableColumnAlignment.CENTER ? 'text-center' : '',
                column.align === UiTableColumnAlignment.LEFT ? 'text-left' : '',
                column.align === UiTableColumnAlignment.RIGHT ? 'text-right' : ''
              ]"
            >
              <InfuseCopy v-if="column.copyable && get(row, column.field)" v-slot="scope" @click.stop="">
                <!-- @click.stop: stop bubbling up -->
                <slot :name="column.name" :data="get(row, column.field)" :row="row" :index="j" :active="active(row, i)">
                  <span @click="scope.copy(get(row, column.field).toString())">{{ get(row, column.field) }}</span>
                </slot>
                <font-awesome-icon
                  class="ml-1 cursor-pointer text-neutral-500 hover:text-neutral-600"
                  :icon="['far', 'copy']"
                  @click="scope.copy(get(row, column.field).toString())"
                />
              </InfuseCopy>
              <slot
                v-else
                :name="column.name"
                :data="get(row, column.field)"
                :row="row"
                :index="j"
                :row-index="i"
                :active="active(row, i)"
              >
                {{ get(row, column.field) }}
              </slot>
            </td>

            <!-- Mobile table -->
            <td class="flex flex-col gap-2 md:hidden">
              <div
                v-for="(column, j) in columns"
                :key="`column-field-attr-${j}`"
                class="flex flex-row gap-2 overflow-hidden break-all"
              >
                <div class="font-bold text-neutral-600">{{ column.label() }}:</div>
                <InfuseCopy v-if="column.copyable && get(row, column.field)" v-slot="scope" @click.stop="">
                  <!-- @click.stop: stop bubbling up -->
                  <slot
                    :name="column.name"
                    :data="get(row, column.field)"
                    :row="row"
                    :index="j"
                    :active="active(row, i)"
                  >
                    <span @click="scope.copy(get(row, column.field).toString())">{{ get(row, column.field) }}</span>
                  </slot>
                  <font-awesome-icon
                    class="ml-1 cursor-pointer text-neutral-500 hover:text-neutral-600"
                    :icon="['far', 'copy']"
                    @click="scope.copy(get(row, column.field).toString())"
                  />
                </InfuseCopy>

                <div v-else :class="{ 'font-semibold text-neutral-700': column.bold }">
                  <slot
                    :name="column.name"
                    :data="get(row, column.field)"
                    :row="row"
                    :index="j"
                    :row-index="i"
                    :active="active(row, i)"
                  >
                    {{ get(row, column.field) }}
                  </slot>
                </div>
              </div>
            </td>

            <td v-if="actions.length > 0" class="whitespace-nowrap py-3 px-6">
              <div class="flex justify-end gap-2">
                <span
                  v-for="(action, idx) in actions"
                  :key="`action-${idx}`"
                  :class="action.onlyShowOnHover && 'invisible group-hover:visible'"
                >
                  <InfuseTooltip :content="action.label()" :offset-distance="'6'" placement="left">
                    <font-awesome-icon
                      v-if="action.enabled(row)"
                      :icon="action.icon.length > 1 ? [action.icon[0], action.icon[1]] : ['fas', action.icon]"
                      :class="action.class ?? 'cursor-pointer text-neutral-400 hover:text-primary-500'"
                      size="lg"
                      @click.stop="$emit('action', action.event, row)"
                    />
                  </InfuseTooltip>
                </span>
              </div>
            </td>

            <td v-if="!row.readonly && (editable || removable || downloadable)" class="py-4 px-6 text-center text-sm">
              <div
                class="invisible flex items-center text-neutral-400 hover:text-neutral-500 group-hover:visible"
                :class="dataTableBodyRowActionsClass"
              >
                <!-- Download -->
                <div
                  v-if="downloadable"
                  v-permission="{
                    ids: downloadPermissionIds,
                    hide: false
                  }"
                  class="p-1 hover:text-primary-500"
                  @click.stop="$emit('download', row)"
                >
                  <slot name="downloadRow">
                    <FontAwesomeIcon :icon="faDownload" size="1x" />
                  </slot>
                </div>

                <!-- Edit -->
                <div
                  v-if="editable"
                  v-permission="{
                    ids: editPermissionIds,
                    hide: false
                  }"
                  class="p-1 hover:text-primary-500"
                  @click.stop="$emit('edit', row)"
                >
                  <slot name="editRow">
                    <font-awesome-icon :icon="['far', 'pen-to-square']" size="1x" />
                  </slot>
                </div>

                <!-- Delete -->
                <div
                  v-if="removable"
                  v-permission="{
                    ids: removePermissionIds,
                    hide: false
                  }"
                  class="p-1 text-neutral-400 hover:text-error-500"
                  @click.stop="$emit('remove', row)"
                >
                  <slot name="deleteRow">
                    <font-awesome-icon :icon="['far', 'trash-can']" size="1x" />
                  </slot>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="flex w-full justify-center p-3">
        <InfuseSpinner size="medium" />
      </div>

      <div v-if="hasMoreItems" class="flex w-full justify-center p-3">
        <InfuseButton :disabled="loading" @click="$emit('load')">
          {{ t('shared.component.infuse.table.loadMore') }}
        </InfuseButton>
      </div>
    </div>

    <div
      v-if="selectedRows.length > 0"
      :class="dataTableCounterClass"
      class="m-auto flex h-12 items-center justify-between"
    >
      <span class="py-3 text-sm">
        {{ `${selectedRows.length} / ${rows.length} ${t('shared.component.infuse.table.itemSelected')}` }}
      </span>
      <slot name="selectionActions" :selected-rows="selectedRows" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { vPermission } from '@infuse/iam/permission/directives';
import {
  ColumnSort,
  Filter,
  FilterConfigurationType,
  Order,
  UiRowAction,
  UiTableColumn,
  UiTableColumnAlignment
} from '@infuse/ui/domain';
import { getFieldFromPath } from '@infuse/utils';
import { useInfiniteScroll } from '@vueuse/core';
import { debounce } from 'lodash';
import { computed, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { InfuseCopy, InfuseIconButton, InfusePill } from '../index';
import InfuseButton from './InfuseButton.vue';
import InfuseCheckBox from './InfuseCheckBox.vue';
import InfuseInput from './InfuseInput.vue';
import InfuseSpinner from './InfuseSpinner.vue';
import InfuseTooltip from './InfuseTooltip.vue';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { hasRequiredPermissions } from '@infuse/ui/utils';

interface Props {
  columns: UiTableColumn[];
  actions?: UiRowAction[];
  rows: any[];
  active?: (row: any, id?: number) => boolean;
  downloadable?: boolean;
  downloadPermissionId?: string;
  downloadPermissionIds?: string[];
  editable?: boolean;
  hasMoreItems?: boolean;
  highlightRow?: number | null;
  loading?: boolean;
  addable?: boolean;
  selectable?: boolean;
  refreshable?: boolean;
  removable?: boolean;
  rowKey?: string;
  search?: {
    query?: string;
    placeholder?: string;
  };
  verticalSize?: 'compact' | 'medium' | 'expanded';
  filters?: Filter[];
  disabledRows?: any[];
  noDataTitle?: string;
  noDataDescription?: string;
  removePermissionId?: string;
  removePermissionIds?: string[];
  editPermissionId?: string;
  editPermissionIds?: string[];
  classVisitor?: (key: string) => string;
  selectCounterInTable?: boolean;
  clickPermissionId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  actions: () => [],
  active: () => false,
  addable: false,
  downloadable: false,
  downloadPermissionId: undefined,
  downloadPermissionIds: undefined,
  editable: false,
  hasMoreItems: true,
  highlightRow: null,
  refreshable: false,
  removable: false,
  rowKey: 'id',
  selectable: false,
  search: undefined,
  verticalSize: 'expanded',
  filters: () => [],
  disabledRows: () => [],
  reverseActions: false,
  noDataTitle: '',
  noDataDescription: '',
  removePermissionId: undefined,
  removePermissionIds: undefined,
  editPermissionId: undefined,
  editPermissionIds: undefined,
  classVisitor: undefined,
  selectCounterInTable: true,
  clickPermissionId: undefined
});

interface Emits {
  (e: 'add'): void;

  (e: 'action', event: string, row: any): void;

  (e: 'click-row', row: any): void;

  (e: 'download', row: any): void;

  (e: 'edit', row: any): void;

  (e: 'load'): void;

  (e: 'refresh'): void;

  (e: 'remove', row: any): void;

  (e: 'remove:filter', filter: Filter): void;

  (e: 'search', query: string): void;

  (e: 'selected', rows: any[]): void;

  (e: 'sort', columnSortState: ColumnSort): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();

const allSelected = ref(false);
const dataTable = ref<HTMLElement | null>(null);

const removePermissionIds = computed(() => {
  if (props.removePermissionIds) {
    return props.removePermissionIds;
  }

  return props.removePermissionId ? [props.removePermissionId] : [];
});

const downloadPermissionIds = computed(() => {
  if (props.downloadPermissionIds) {
    return props.downloadPermissionIds;
  }

  return props.downloadPermissionId ? [props.downloadPermissionId] : [];
});

const editPermissionIds = computed(() => {
  if (props.editPermissionIds) {
    return props.editPermissionIds;
  }

  return props.editPermissionId ? [props.editPermissionId] : [];
});

const debounceSearch = debounce((query: string) => {
  if (query !== props.search?.query) {
    emit('search', query);
  }
}, 500);

const selectedRows: Ref<any[]> = ref([]);

const columnsWithSortState = computed(() =>
  props.columns.map((c) => ({ ...c, state: { columnName: c.name, order: Order.NONE } }))
);

useInfiniteScroll(dataTable, () => {
  if (!props.loading) {
    emit('load');
  }
});

function selectAll() {
  selectedRows.value = [];
  props.rows.forEach((row: any) => {
    selectedRows.value.push(row);
  });
  emit('selected', selectedRows.value);
}

function deselectAll() {
  selectedRows.value = [];
  emit('selected', selectedRows.value);
}

function sort(columnSortState: ColumnSort): void {
  switch (columnSortState.order) {
    case Order.NONE:
      columnSortState.order = Order.DESC;
      break;
    case Order.DESC:
      columnSortState.order = Order.ASC;
      break;
    case Order.ASC:
      columnSortState.order = Order.NONE;
      break;
  }

  emit('sort', columnSortState);
}

function getSortIcon(order: Order): [string, string] {
  switch (order) {
    case Order.NONE:
      return ['fas', 'sort'];
    case Order.DESC:
      return ['fad', 'sort-down'];
    case Order.ASC:
      return ['fad', 'sort-up'];
  }
}

function selectRow(isSelected: boolean, row: any) {
  if (isSelected) {
    selectedRows.value.push(row);
  } else {
    const index = selectedRows.value.indexOf(row);
    if (index !== -1) {
      selectedRows.value.splice(index, 1);
    }

    allSelected.value = false;
  }

  emit('selected', selectedRows.value);
}

function clickRow(row: any) {
  if (props.clickPermissionId && !hasRequiredPermissions([props.clickPermissionId], 'all', false)) {
    return;
  }
  emit('click-row', row);
}

const get = (obj: unknown, path: any, defValue?: unknown) => getFieldFromPath(obj, path, defValue);

const determineOperator = (filter: Filter): string => {
  if (filter.config.type === FilterConfigurationType.GT || filter.config.type === FilterConfigurationType.STRING_GT) {
    return filter.negated ? '≤' : '>';
  } else if (
    filter.config.type === FilterConfigurationType.GTE ||
    filter.config.type === FilterConfigurationType.STRING_GTE
  ) {
    return filter.negated ? '<' : '≥';
  } else if (
    filter.config.type === FilterConfigurationType.LT ||
    filter.config.type === FilterConfigurationType.STRING_LT
  ) {
    return filter.negated ? '≥' : '<';
  } else if (
    filter.config.type === FilterConfigurationType.LTE ||
    filter.config.type === FilterConfigurationType.STRING_LTE
  ) {
    return filter.negated ? '>' : '≤';
  }
  return filter.negated ? t('shared.component.infuse.table.filter.negated') : '';
};

const dataTableClass = ref('overflow-x-auto');
const dataTableHeadClass = ref('bg-neutral-50 text-neutral-500 top-0');
const dataTableHeadFilterClass = ref('flex w-full items-center gap-4');
const dataTableBodyClass = ref("before:block before:leading-5 before:text-transparent before:content-['@']");
const dataTableBodyRowClass = ref('text-neutral-500 hover:bg-neutral-50/[50%]');
const dataTableBodyRowActionsClass = ref('text-neutral-500 hover:bg-neutral-50/[50%]');
const dataTableCounterClass = ref(
  'absolute bottom-3 left-0 right-0 w-[36rem] rounded border border-gray-100 bg-white px-7 shadow'
);

watch(
  () => props.classVisitor,
  (visitorFn) => {
    if (typeof visitorFn === 'function') {
      dataTableClass.value = visitorFn('dataTableClass');
      dataTableHeadClass.value = visitorFn('dataTableHeadClass');
      dataTableHeadFilterClass.value = visitorFn('dataTableHeadFilterClass');
      dataTableBodyClass.value = visitorFn('dataTableBodyClass');
      dataTableBodyRowClass.value = visitorFn('dataTableBodyRowClass');
      dataTableBodyRowActionsClass.value = visitorFn('dataTableBodyRowActionsClass');
      dataTableCounterClass.value = visitorFn('dataTableCounterClass');
    }
  },
  { immediate: true }
);
</script>
